import {
  Box,
  mdBumps,
  ModalHeader,
  SectionProps,
  Typo,
  useModal,
} from '@wrisk/ui-components'
import React, { FunctionComponent, useCallback } from 'react'

import { getCurrentQuote, OptionalCover } from '../../../domain'
import {
  useProposalAdjustment,
  useProposalFormData,
} from '../../../hooks/adjustments/proposal'
import { useCoverLevelOption } from '../../../hooks/coverOption'
import {
  TExtends,
  TKey,
  useWriskTranslation,
} from '../../../infrastructure/internationalisation'
import { FormConfig } from '../../../state/configuration'
import { FormDisabled } from '../../formBuilder'
import { useProduct } from '../../product/ProductProvider'
import { useProposal } from '../../product/proposal/ProposalProvider'
import { CoverSection } from '../covers'

export const ProposalCoverSection: FunctionComponent<SectionProps> = (props) => {
  const modal = useModal()

  const { proposal } = useProposal()
  const { product } = useProduct()

  const quote = getCurrentQuote(proposal)

  const { coverLevelConfig } = useCoverLevelOption(quote, product)

  const onChange = useCallback(
    (coverOption: OptionalCover) => {
      const { form } =
        coverLevelConfig?.covers?.optional?.find(
          (it) => it.code === coverOption.coverOption,
        ) ?? {}

      if (!form) {
        throw new Error(`No form found for optional cover ${coverOption.coverOption}`)
      }

      modal.show({
        content: <ProposalEditOptionalCoverModal form={form} onComplete={modal.hide} />,
      })
    },
    [coverLevelConfig, modal],
  )

  return <CoverSection quote={quote} onChange={onChange} {...props} />
}

const tKey = TKey('components.edit-optional-cover-modal')

interface EditOptionalCoverModalProps {
  form: FormConfig
  onComplete: () => void
}

const ProposalEditOptionalCoverModal: FunctionComponent<EditOptionalCoverModalProps> = ({
  form,
  onComplete,
}) => {
  const { t } = useWriskTranslation()

  const data = useProposalFormData(form)
  const onSubmitAdjustment = useProposalAdjustment({
    form: form,
    applyRating: true,
    onComplete: onComplete,
  })

  return (
    <Box>
      <ModalHeader header={t(tKey('header'))} />
      <Box marginBottom={mdBumps}>
        <Typo>{t(tKey('description'))}</Typo>
      </Box>
      <FormDisabled
        id='editOptionalCover'
        data={data}
        config={form}
        onSubmit={onSubmitAdjustment.execute}
        loading={onSubmitAdjustment.loading}
        tKey={TExtends(tKey, 'inputs')}
        t={t}
      />
    </Box>
  )
}
